<template>
  <div id="app" class="bg-transparent relative md:mx-auto md:max-w-2xl">
    <loading v-show="!isMobileApp" ref="loading" />
    <transition name="">
      <!--   Connection issues   -->
      <div v-if="!connected" class="min-h-screen flex flex-col bg-transparent pb-20 relative justify-center">
        <h1 class="text-center text-5xl">
          🤕
        </h1>
        <p class="font-semibold text-center mt-4">
          {{$t('app.noInternet.title')}}
        </p>
        <p class="text-center mt-2">
          {{$t('app.noInternet.description')}}
        </p>
      </div>
      <component :is="layout" v-else-if="layout" />
    </transition>
    <portal-target name="modals" multiple />
    
    <!-- App Store Update Modal -->
    <Modal :show="showAppStoreUpdatePrompt" @close="dismissAppStoreUpdatePrompt" icon-color="purple">
      <template #icon>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
        </svg>
      </template>
      <template #title>
        {{ $t('app.appStoreUpdate.title') }}
      </template>
      <div class="text-center text-gray-600 mb-8">
        <p class="mb-4">{{ $t('app.appStoreUpdate.description') }}</p>
        <p class="text-sm text-gray-500">
          {{ $t('app.appStoreUpdate.newVersion') }}: {{ appStoreVersion }}
        </p>
      </div>
      <div class="flex justify-center gap-4">
        <v-button @click="openAppStore" color="purple">
          {{ $t('app.appStoreUpdate.update') }}
        </v-button>
      </div>
    </Modal>
    
    <notifications />
  </div>
</template>

<script>
import Loading from './Loading.vue'
import StopImpersonation from './pages/StopImpersonation.vue'
import Notifications from './common/Notifications.vue'
import { SplashScreen } from '@capacitor/splash-screen'
import { mapGetters, mapActions } from 'vuex'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { Network } from '@capacitor/network'
import { captureException } from '@sentry/browser';
import PushNotificationService from '~/services/PushNotificationService'
import { Device } from '@capacitor/device'
import pkg from '../../../package.json'
import Modal from './Modal.vue'

// Load layout components dynamically.
const requireContext = import.meta.glob('../layouts/**.vue', { eager: true })

const layouts = {}
Object.keys(requireContext)
  .map(file =>
    [file.match(/[^/]*(?=\.[^.]*$)/)[0], requireContext[file]]
  )
  .forEach(([name, component]) => {
    layouts[name] = component.default || component
  })

export default {
  el: '#app',

  components: {
    Notifications,
    StopImpersonation,
    Loading,
    Modal
  },

  data: () => ({
    layout: null,
    defaultLayout: 'default',
    connected: true,
    pushNotificationService: null,
    appStoreVersion: pkg.appStoreVersion,
    appCurrent: null,
    showAppStoreUpdatePrompt: false
  }),
  computed: {
    ...mapGetters({
      authenticated: 'auth/check',

      user: 'auth/user'
    })
  },
  methods: {
    ...mapActions({

      fetchPeriodDates: 'periods/fetchPeriodDates'
    }),
    /**
     * Set the application layout.
     *
     * @param {String} layout
     */
    setLayout (layout) {
      if (!layout || !layouts[layout]) {
        layout = this.defaultLayout
      }

      this.layout = layouts[layout]
    },
    /**
     * Compare versions ignoring patch numbers
     * Returns true if newVersion > currentVersion based on major.minor only
     */
    isVersionUpdateAvailable(currentVersion, newVersion) {
      const parseVersion = (version) => {
        const parts = version.split('.').map(Number);
        return {
          major: parts[0] || 0,
          minor: parts[1] || 0,
          patch: parts[2] || 0
        };
      };
      
      const current = parseVersion(currentVersion);
      const latest = parseVersion(newVersion);
      
      // Check major version first
      if (latest.major > current.major) {
        return true;
      }
      
      // If major versions are equal, check minor version
      if (latest.major === current.major && latest.minor > current.minor) {
        return true;
      }
      
      // Ignore patch version differences
      return false;
    },
    async checkForAppStoreUpdates() {
      if (!this.appCurrent) return;
      
      const currentVersion = this.appCurrent.version || pkg.version;
      if (this.isVersionUpdateAvailable(currentVersion, this.appStoreVersion)) {
        this.showAppStoreUpdatePrompt = true;
      }
    },

    async initializePushNotifications() {
      if (!this.isMobileApp) return;
      
      if (!this.pushNotificationService) {
        this.pushNotificationService = new PushNotificationService();
      }
      
      try {
        await this.pushNotificationService.askForNotificationPermission();
        this.pushNotificationService.initializeListeners(this.saveNotificationToken);
      } catch (error) {
        captureException(error)
        console.error('Error with push notification service:', error);
      }
    },
    async saveNotificationToken(token) {
      try {
        // Check if user is authenticated before updating
        if (!this.user || !this.user.notification_details) return
        if(this.user.notification_details['token'] == token.value) return
        const deviceInfo = await Device.getInfo()
        await this.$store.dispatch('auth/updateUser', {'notification_details':{
            token: token.value,
            platform: deviceInfo.platform
        }});
      } catch (error) {
        captureException(error)
        console.error('Error saving notification token:', error)
      }
    },
    dismissAppStoreUpdatePrompt() {
      this.showAppStoreUpdatePrompt = false;
    },
    openAppStore() {
      // Open the App Store for iOS or Play Store for Android
      if (this.isMobileApp) {
        const appStoreUrl = 'https://apps.apple.com/en/app/moone-period-cycle-tracker/id6739633389';
        window.open(appStoreUrl, '_system');
      }
      
      this.dismissAppStoreUpdatePrompt();
    },
    setClarityUserId() {
      if (window.clarity && typeof window.clarity === 'function' && this.user && this.user.id) {
        this.$setClarityUserId(this.user.id);
      }
    }
  },
  watch: {
    async authenticated(newValue) {
      if (newValue) {
        try{
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
          await this.$store.dispatch('auth/updateUser', { timezone })
        }catch(error){
          captureException(error)
        }
        await this.checkForAppStoreUpdates();
        await this.$store.dispatch('auth/fetchUser')
        
        // Set Clarity user ID after user is fetched
        this.setClarityUserId()
        
        await this.$store.dispatch('onboarding/fetchOnboardingSteps')
        await this.$store.dispatch('dailyDashboard/fetchDashboardData')

        await this.$store.dispatch('reports/fetchReports')
        await this.$store.dispatch('dailyDashboard/fetchStreak')
        
        // Fetch period dates when authenticated
        await this.fetchPeriodDates()
        
        // Initialize push notification listeners when authenticated
        await this.initializePushNotifications()
      }
    }
  },
  async mounted () {
    if (this.isMobileApp) {
      CapacitorUpdater.notifyAppReady()
      this.appCurrent = await CapacitorUpdater.current()

      this.setStatusBarStyle('light')
      
      try {
        SplashScreen.hide()
      } catch (e) {}
      
      // Make sure internet is connected
      Network.addListener('networkStatusChange', status => {
        this.connected = status.connected
      })
      
    }

    this.$loading = this.$refs.loading
    
    if (this.authenticated) {
      try {
        await this.checkForAppStoreUpdates();
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        await this.$store.dispatch('auth/updateUser', { timezone })
        await this.$store.dispatch('auth/fetchUser')
        
        this.setClarityUserId()
        
        await this.$store.dispatch('onboarding/fetchOnboardingSteps')

        await this.$store.dispatch('dailyDashboard/fetchDashboardData')
        await this.$store.dispatch('dailyDashboard/fetchStreak')

        await this.$store.dispatch('reports/fetchReports')

        // Fetch period dates when authenticated
        await this.fetchPeriodDates()

        // Initialize push notification listeners when authenticated
        await this.initializePushNotifications()
        

      } catch (error) {
        captureException(error)
      }
    }
  }
}
</script>